'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { type Editor, type Range } from '@tiptap/core';
import { type SlashCommandItem, type SlashCommandRenderState } from '../lib/tiptap/extensions/SlashCommand';
import { useRunnerRequestUiRuntime, type SearchProvider } from '../runtime';

const commandToProvider: Partial<Record<string, SearchProvider>> = {
  github: 'github',
  gitlab: 'gitlab',
  bitbucket: 'bitbucket',
  linear: 'linear',
  sentry: 'sentry',
};
const isExternalProviderCommand = (id: string) => Boolean(commandToProvider[id]);

export type SlashCommandPopupRef = {
  onKeyDown: (event: KeyboardEvent) => boolean;
};

type SlashCommandPopupProps = {
  editor: Editor;
  range: Range;
  query: string;
  items: SlashCommandItem[];
  clientRect: SlashCommandRenderState['clientRect'];
  token: string | null;
  projectId: string;
  onCloseAction: () => void;
  onSelectCommandAction: (command: SlashCommandItem) => void;
  portalContainer?: React.RefObject<HTMLElement | null>;
};

export const SlashCommandPopup = forwardRef<SlashCommandPopupRef, SlashCommandPopupProps>(
  (
    {
      editor,
      range,
      query,
      items,
      clientRect,
      token,
      projectId,
      onCloseAction,
      onSelectCommandAction,
      portalContainer,
    },
    ref,
  ) => {
    const { cacheAvailableProviders, getCachedAvailableProviders, searchProjectEntities } = useRunnerRequestUiRuntime();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [availableProviders, setAvailableProviders] = useState<SearchProvider[] | null>(
      getCachedAvailableProviders(projectId),
    );
    const [popupStyle, setPopupStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden' });
    const popupRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

    const isProvidersLoading = availableProviders === null;
    const hasExternalCommands = items.some((item) => isExternalProviderCommand(item.id));

    const visibleCommandItems = useMemo(
      () =>
        items.filter((item) => {
          const provider = commandToProvider[item.id];
          if (!provider) {
            return true;
          }

          return availableProviders?.includes(provider) ?? false;
        }),
      [items, availableProviders],
    );

    useEffect(() => {
      setSelectedIndex(0);
    }, [query, range.from, range.to]);

    useEffect(() => {
      if (!token || !projectId) {
        setAvailableProviders([]);
        return;
      }

      const cached = getCachedAvailableProviders(projectId);
      if (cached) {
        setAvailableProviders(cached);
        return;
      }

      let isCancelled = false;
      void searchProjectEntities(token, projectId, { q: '' })
        .then((data) => {
          if (isCancelled) return;
          cacheAvailableProviders(projectId, data.meta.availableProviders);
          setAvailableProviders(data.meta.availableProviders);
        })
        .catch(() => {
          if (isCancelled) return;
          setAvailableProviders([]);
        });

      return () => {
        isCancelled = true;
      };
    }, [projectId, token]);

    useEffect(() => {
      setSelectedIndex(0);
    }, [items, availableProviders]);

    useEffect(() => {
      const selectedItem = optionRefs.current[selectedIndex];
      selectedItem?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex, visibleCommandItems]);

    useEffect(() => {
      if (!clientRect) {
        setPopupStyle({ position: 'fixed', visibility: 'hidden' });
        return;
      }

      const container = portalContainer?.current;

      const updatePosition = () => {
        const rect = clientRect();

        if (!rect) {
          return;
        }

        const popupMaxHeight = 320;
        const popupMinBelow = 80;
        const popupWidth = 288;
        const padding = 12;
        const popupZIndex = 1100;

        if (container) {
          const containerRect = container.getBoundingClientRect();
          const cursorTop = rect.top - containerRect.top + container.scrollTop;
          const cursorBottom = rect.bottom - containerRect.top + container.scrollTop;
          const cursorLeft = rect.left - containerRect.left + container.scrollLeft;

          const visibleTop = container.scrollTop;
          const visibleBottom = container.scrollTop + container.clientHeight;
          const spaceBelow = visibleBottom - cursorBottom - padding;
          const spaceAbove = cursorTop - visibleTop - padding;
          const shouldOpenAbove = spaceBelow < popupMinBelow && spaceAbove > spaceBelow;
          const effectiveMaxHeight = Math.min(popupMaxHeight, Math.max(shouldOpenAbove ? spaceAbove : spaceBelow, 0));

          const left = Math.min(Math.max(cursorLeft, padding), container.clientWidth - popupWidth - padding);

          setPopupStyle({
            position: 'absolute',
            top: shouldOpenAbove ? cursorTop - 6 : cursorBottom + 6,
            left,
            width: popupWidth,
            maxHeight: effectiveMaxHeight,
            zIndex: popupZIndex,
            ...(shouldOpenAbove ? { transform: 'translateY(-100%)' } : {}),
          });
        } else {
          const vv = window.visualViewport;
          const viewportTop = vv?.offsetTop ?? 0;
          const viewportHeight = vv?.height ?? window.innerHeight;
          const viewportWidth = vv?.width ?? window.innerWidth;
          const viewportBottom = viewportTop + viewportHeight;

          const left = Math.min(Math.max(rect.left, padding), viewportWidth - popupWidth - padding);
          const spaceBelow = viewportBottom - rect.bottom - padding;
          const spaceAbove = rect.top - viewportTop - padding;
          const shouldOpenAbove = spaceBelow < popupMinBelow && spaceAbove > spaceBelow;
          const effectiveMaxHeight = Math.min(popupMaxHeight, Math.max(shouldOpenAbove ? spaceAbove : spaceBelow, 0));

          setPopupStyle({
            position: 'fixed',
            top: shouldOpenAbove ? rect.top - 6 + viewportTop : rect.bottom + 6 + viewportTop,
            left,
            width: popupWidth,
            maxHeight: effectiveMaxHeight,
            zIndex: popupZIndex,
            ...(shouldOpenAbove ? { transform: 'translateY(-100%)' } : {}),
          });
        }
      };

      updatePosition();

      const resizeTarget = window.visualViewport ?? window;
      resizeTarget.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        resizeTarget.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }, [clientRect, portalContainer]);

    useEffect(() => {
      const element = popupRef.current;
      if (!element) return;

      let startY = 0;

      const onTouchStart = (e: TouchEvent) => {
        startY = e.touches[0].clientY;
      };

      const onTouchMove = (e: TouchEvent) => {
        const { scrollTop, scrollHeight, clientHeight } = element;
        const isScrollable = scrollHeight > clientHeight;

        if (!isScrollable) {
          e.preventDefault();
          return;
        }

        const deltaY = e.touches[0].clientY - startY;
        const atTop = scrollTop <= 0;
        const atBottom = scrollTop + clientHeight >= scrollHeight;

        if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
          e.preventDefault();
        }
      };

      element.addEventListener('touchstart', onTouchStart, { passive: true });
      element.addEventListener('touchmove', onTouchMove, { passive: false });
      return () => {
        element.removeEventListener('touchstart', onTouchStart);
        element.removeEventListener('touchmove', onTouchMove);
      };
    }, [popupStyle]);

    const handleKeyDown = (event: KeyboardEvent) => {
      const maxIndex = visibleCommandItems.length - 1;

      if (event.key === 'ArrowUp') {
        if (maxIndex < 0) {
          return true;
        }
        event.preventDefault();
        setSelectedIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
        return true;
      }

      if (event.key === 'ArrowDown') {
        if (maxIndex < 0) {
          return true;
        }
        event.preventDefault();
        setSelectedIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (visibleCommandItems[selectedIndex]) {
          onSelectCommandAction(visibleCommandItems[selectedIndex]);
        }
        return true;
      }

      if (event.key === 'Escape') {
        onCloseAction();
        editor.commands.focus();
        return false;
      }

      return false;
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: handleKeyDown,
    }));

    const renderCommandRow = (item: SlashCommandItem, index: number) => (
      <button
        key={item.id}
        ref={(element) => {
          optionRefs.current[index] = element;
        }}
        type="button"
        className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors ${
          index === selectedIndex ? 'bg-base-200' : 'active:bg-base-200'
        }`}
        onClick={() => onSelectCommandAction(item)}
        onMouseMove={() => {
          if (selectedIndex !== index) {
            setSelectedIndex(index);
          }
        }}
      >
        {item.icon as ReactNode}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-base-content">{item.title}</div>
          <div className="truncate text-xs text-[var(--color-fg-muted)]">{item.description}</div>
        </div>
      </button>
    );

    if (typeof document === 'undefined') {
      return null;
    }

    const portalTarget = portalContainer?.current ?? document.body;

    return createPortal(
      <div
        ref={popupRef}
        style={popupStyle}
        className={`${portalContainer?.current ? '' : 'max-h-80 '}overflow-y-auto overscroll-contain rounded-lg border border-base-300 bg-base-100 p-1 shadow-lg`}
      >
        {visibleCommandItems.length === 0 && !isProvidersLoading ? (
          <div className="p-4 text-center text-sm text-[var(--color-fg-muted)]">No commands found</div>
        ) : (
          <>
            {visibleCommandItems.map(renderCommandRow)}
            {isProvidersLoading && hasExternalCommands ? (
              <div className="space-y-1">
                {[0, 1].map((value) => (
                  <div key={value} className="flex w-full items-center gap-2 rounded-md px-3 py-2">
                    <div className="skeleton h-5 w-5 shrink-0 rounded" />
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="skeleton h-4 w-2/3 rounded" />
                      <div className="skeleton h-3 w-1/2 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>,
      portalTarget,
    );
  },
);

SlashCommandPopup.displayName = 'SlashCommandPopup';
